import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Globe, ArrowLeft, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLanguage } from "../context/LanguageContext";
import axios from "axios";
import DOMPurify from "dompurify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "34688853678";

export default function CustomPageView() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPage = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pages/${slug}`);
      setPage(response.data);
    } catch (err) {
      setError("Página no encontrada");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-neutral-500">Cargando...</div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">404</h1>
          <p className="text-neutral-500 mb-8">{error || "Página no encontrada"}</p>
          <Link to="/">
            <Button className="bg-orange-500 hover:bg-orange-600 rounded-full">
              Volver al inicio
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const title = language === "es" ? page.title_es : page.title_en;
  const content = language === "es" ? page.content_es : page.content_en;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="glass border-b border-neutral-800/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center shadow-lg">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg tracking-tight text-white font-['Outfit']">
                Global Tech Xpert
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="rounded-full border-neutral-700 text-neutral-300 hover:bg-neutral-800 gap-2">
                <ArrowLeft className="w-4 h-4" />
                {language === "es" ? "Volver" : "Back"}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl font-bold text-white font-['Outfit'] mb-8">{title}</h1>
        
        <div 
          className="prose prose-invert prose-lg max-w-none
            prose-headings:font-['Outfit'] prose-headings:text-white
            prose-p:text-neutral-300
            prose-a:text-orange-400 prose-a:no-underline hover:prose-a:underline
            prose-strong:text-white
            prose-ul:text-neutral-300 prose-ol:text-neutral-300"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content || "") }}
        />

        {/* CTA */}
        <div className="mt-12 p-8 glass rounded-3xl text-center">
          <h3 className="text-xl font-bold text-white mb-4">
            {language === "es" ? "¿Te interesa?" : "Interested?"}
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/#contacto">
              <Button className="bg-orange-500 hover:bg-orange-600 rounded-full px-8 py-6 text-lg font-semibold">
                {language === "es" ? "Pedir cotización" : "Get a quote"}
              </Button>
            </Link>
            <a 
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-[#25D366] hover:bg-[#20BD5C] rounded-full px-8 py-6 text-lg font-semibold gap-2">
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-neutral-800 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-neutral-500 text-sm">
          © {new Date().getFullYear()} Global Tech Xpert
        </div>
      </footer>
    </div>
  );
}
